import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetcher } from 'utils/axios';
import useAuth from 'hooks/useAuth';
import useLocalization from 'hooks/useLocalization';
import { getDynamicDashboardPath } from 'config';

// MUI
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  Fade,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

type MeResponse = {
  companyId: string | null;
  needsOnboarding: boolean;
};

type MeFetchResult =
  | { ok: true; data: MeResponse }
  | { ok: false; reason: 'network' };

/**
 * `fetcher` returns null on HTTP/network failure — must not be treated as needsOnboarding: false,
 * or we navigate to /login and GuestGuard sends the user back to /company-onboarding (infinite loop).
 *
 * הבדיקה עוברת דרך `/api/users/me` (ולא `/api/onboarding/me`) בכוונה: users/me
 * מכיל את מנגנון האימוץ לפי אימייל של משתמשים מועברים מ-web2 — משתמש כזה
 * שנוחת כאן בדפדפן נקי (GuestGuard מפנה לפי localStorage ריק, לפני ש-users/me
 * הספיק לרוץ) מאומץ כאן ומנותב ישר לחברה שלו. onboarding/me בודק רק
 * ClerkUserId והיה משאיר משתמשים מועברים תקועים על טופס פתיחת ארגון.
 */
const checkCompanyExistOnThisUser = async (): Promise<MeFetchResult> => {
  const data = await fetcher('get', '/api/users/me', 'app');
  if (data == null) {
    return { ok: false, reason: 'network' };
  }
  return { ok: true, data: data as MeResponse };
};

function LoadingSkeleton() {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        py: { xs: 4, md: 6 },
      }}
    >
      <Container maxWidth="sm">
        <Stack spacing={3}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Skeleton variant="rounded" width={52} height={52} sx={{ borderRadius: 2 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Skeleton variant="text" sx={{ fontSize: 28, width: '72%', maxWidth: 360 }} />
              <Skeleton variant="text" sx={{ mt: 0.5 }} />
              <Skeleton variant="text" width="92%" />
            </Box>
          </Stack>
          <Skeleton variant="rounded" height={220} sx={{ borderRadius: 3 }} />
        </Stack>
      </Container>
    </Box>
  );
}

export default function OnboardingCompany() {
  const navigate = useNavigate();
  const { currentLocale } = useLocalization();
  const loadErrorCopy = currentLocale.companyOnboarding.loadError;
  const loc = currentLocale.companyOnboarding;

  const [checkingCompanyStatus, setCheckingCompanyStatus] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    let isMounted = true;

    const checkCompany = async () => {
      setLoadError(null);
      setCheckingCompanyStatus(true);

      let result: MeFetchResult;
      try {
        result = await checkCompanyExistOnThisUser();
      } catch {
        if (!isMounted) return;
        setLoadError(loadErrorCopy);
        setCheckingCompanyStatus(false);
        return;
      }

      if (!isMounted) return;

      if (!result.ok) {
        setLoadError(loadErrorCopy);
        setCheckingCompanyStatus(false);
        return;
      }

      const { companyId, needsOnboarding } = result.data;

      if (companyId) {
        localStorage.setItem('companyId', companyId);
        navigate(getDynamicDashboardPath(), { replace: true });
      } else if (!needsOnboarding) {
        navigate('/login', { replace: true });
      } else {
        setCheckingCompanyStatus(false);
      }
    };

    checkCompany();

    return () => {
      isMounted = false;
    };
  }, [navigate, retryToken, loadErrorCopy]);

  const retryLoad = useCallback(() => {
    setRetryToken((t) => t + 1);
  }, []);
  const { logout } = useAuth();

  const handleBackFromError = useCallback(async () => {
    try {
      await logout();
    } catch {
      /* ignore */
    } finally {
      localStorage.removeItem('companyId');
      localStorage.removeItem('whatsapp-token');
      navigate('/login', { replace: true });
    }
  }, [logout, navigate]);

  const [companyName, setCompanyName] = useState('');
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const trimmed = companyName.trim();

  const validation = useMemo(() => {
    if (!trimmed) return loc.validationRequired;
    if (trimmed.length < 2) return loc.validationTooShort;
    if (trimmed.length > 80) return loc.validationTooLong;
    return null;
  }, [trimmed, loc.validationRequired, loc.validationTooShort, loc.validationTooLong]);

  const canSubmit = !loading && !validation;

  if (checkingCompanyStatus) {
    return <LoadingSkeleton />;
  }

  if (loadError) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          py: { xs: 4, md: 6 },
        }}
      >
        <Container maxWidth="sm">
          <Fade in timeout={400}>
            <Stack spacing={2}>
              <Alert severity="error" sx={{ textAlign: 'start' }}>
                {loadError}
              </Alert>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button variant="contained" onClick={retryLoad} fullWidth>
                  {loc.retry}
                </Button>
                <Button variant="outlined" onClick={handleBackFromError} fullWidth>
                  {loc.backToLogin}
                </Button>
              </Stack>
            </Stack>
          </Fade>
        </Container>
      </Box>
    );
  }

  const handleBack = async () => {
    try {
      setLoading(true);
      await logout();
    } catch (error) {
      console.warn('Logout from onboarding failed:', error);
    } finally {
      localStorage.removeItem('companyId');
      localStorage.removeItem('whatsapp-token');
      setLoading(false);
      navigate('/login', { replace: true });
    }
  };

  const submit = async () => {
    setTouched(true);
    setErrorMsg(null);

    if (validation) return;

    setLoading(true);
    let error = null;
    try {
      const me = (await fetcher(
        'post',
        ['/api/onboarding/company', { data: { companyName: trimmed } }],
        'app',
      )) as MeResponse | null;

      if (!me?.companyId) {
        error = loc.errorCreateFailed;
      } else {
        localStorage.setItem('companyId', me.companyId);
        navigate(getDynamicDashboardPath(), { replace: true });
      }
    } catch (e: any) {
      error =
        typeof e === 'string' ? e : e?.message || loc.errorGeneric;
    }
    setLoading(false);
    if (error) setErrorMsg(error);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        py: { xs: 4, md: 6 },
      }}
    >
      <Container maxWidth="sm">
        <Fade in timeout={400}>
          <Stack spacing={3}>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: 2,
                  flexShrink: 0,
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: 'primary.lighter',
                  color: 'primary.main',
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <BusinessRoundedIcon sx={{ fontSize: 28 }} aria-hidden />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
                <Typography
                  variant="h3"
                  component="h1"
                  sx={{ fontWeight: 700, textAlign: 'start', letterSpacing: -0.5 }}
                >
                  {loc.title}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 1.25, textAlign: 'start', lineHeight: 1.6 }}>
                  {loc.subtitle}
                </Typography>
              </Box>
            </Stack>

            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: (t) => `1px solid ${t.palette.divider}`,
                overflow: 'hidden',
              }}
            >
              <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                <Stack spacing={2.25}>
                  {errorMsg && (
                    <Alert severity="error" sx={{ textAlign: 'start' }}>
                      {errorMsg}
                    </Alert>
                  )}

                  <TextField
                    label={loc.companyNameLabel}
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    onBlur={() => setTouched(true)}
                    placeholder={loc.companyNamePlaceholder}
                    fullWidth
                    autoFocus
                    error={touched && !!validation}
                    helperText={touched && validation ? validation : ' '}
                    inputProps={{ maxLength: 80, 'aria-required': true }}
                  />

                  <Divider />

                  <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1.5}>
                    <Button
                      variant="outlined"
                      fullWidth
                      disabled={loading}
                      onClick={handleBack}
                      sx={{ borderRadius: 2 }}
                    >
                      {loc.back}
                    </Button>

                    <Button
                      variant="contained"
                      color="secondary"
                      fullWidth
                      disabled={!canSubmit}
                      onClick={submit}
                      sx={{ borderRadius: 2 }}
                      startIcon={
                        loading ? (
                          <CircularProgress size={18} color="inherit" aria-hidden />
                        ) : undefined
                      }
                    >
                      {loading ? loc.submitting : loc.continue}
                    </Button>
                  </Stack>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', textAlign: 'start', lineHeight: 1.6 }}
                  >
                    {loc.footerNote}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Fade>
      </Container>
    </Box>
  );
}
